import React, { FC, useEffect, useId, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSignupWizard } from './signup-wizard-context';
import WizardLayout from './wizard-layout';
import StepHeader from './step-header';
import ErrorAlert from './error-alert';
import { CheckOrgResponse, ServiceSupplierSignup } from '../../services/service-supplier-signup';
import { CompanySearchRow } from '../../services/service-company-search';
import CompanySearch from '../app-company-search/CompanySearch';
import { CompanySearchUnavailable } from '../app-company-search/CompanySearchView';
import useCompanySearchAvailability from '../app-company-search/useCompanySearchAvailability';
import BtButton from '../bt-button/bt-button';
import Field from '../../components/shared/Field/Field';
import StyledCheckbox from '../../components/shared/StyledCheckbox/StyledCheckbox';
import Spinner from '../../components/shared/Spinner/Spinner';
import styles from './step-company.module.scss';

const ORG_NUMBER_REGEX = /^\d{6}-\d{4}$/;

const formatOrgNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  return digits.length > 6 ? digits.slice(0, 6) + '-' + digits.slice(6) : digits;
};

type CompanyAddress = { address: string; city: string; postalCode: string };

type Lookup =
  | { status: 'idle' }
  | { status: 'checking'; orgNumber: string }
  | { status: 'failed'; orgNumber: string }
  | { status: 'exists'; orgNumber: string; admins: CheckOrgResponse['admins'] }
  | { status: 'available'; orgNumber: string; synaName: string | null; address: CompanyAddress };

const StepCompany: FC = () => {
  const { t } = useTranslation('newSupplier', { keyPrefix: 'wizard' });
  const history = useHistory();
  const { state, updateState, completeStep, setCurrentStep, trackEvent, withSource } =
    useSignupWizard();
  const nameHelperId = useId();
  const latestLookup = useRef(0);
  const { down, onUnavailable, typedFieldRef } = useCompanySearchAvailability();

  // Formatted so a prefilled org number (which may arrive without the dash)
  // passes the field's validation.
  const [savedOrgNumber] = useState(() => formatOrgNumber(state.organizationNumber || ''));
  const [pick, setPick] = useState(() =>
    state.companyPick?.orgNumber === savedOrgNumber ? state.companyPick : null,
  );
  const [typedOrgNumber, setTypedOrgNumber] = useState(pick ? '' : savedOrgNumber);
  // Decided once: re-deriving it would swap the typed org-number field for the search mid-typing.
  const [showsTypedOrgNumber] = useState(() => ORG_NUMBER_REGEX.test(typedOrgNumber));
  const [companyName, setCompanyName] = useState(state.companyName || '');
  const [formatError, setFormatError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(!!state.termsAcceptedAt);
  const [lookup, setLookup] = useState<Lookup>(() =>
    state.completedSteps.includes(1)
      ? {
          status: 'available',
          orgNumber: savedOrgNumber,
          synaName: state.companyName,
          address: { address: state.address, city: state.city, postalCode: state.postalCode },
        }
      : { status: 'idle' },
  );

  const organizationNumber = pick?.orgNumber ?? typedOrgNumber;
  const current =
    lookup.status !== 'idle' && lookup.orgNumber === organizationNumber ? lookup : null;
  const checking = current?.status === 'checking';
  const checked = current?.status === 'available' ? current : null;
  const existingAdmins = current?.status === 'exists' ? current.admins : null;
  const lookupError =
    current?.status === 'failed'
      ? t('company.lookupError', 'Kunde inte slå upp organisationsnumret. Försök igen.')
      : '';
  const typed = showsTypedOrgNumber || down !== null;
  const nameIsEditable = pick !== null || checked?.synaName === null;
  const companyNameToSave = nameIsEditable ? companyName : checked?.synaName ?? '';
  const canProceed = checked !== null && companyNameToSave.trim().length > 0 && termsAccepted;

  const lookUp = async (orgNumber: string) => {
    latestLookup.current += 1;
    const request = latestLookup.current;
    setLookup({ status: 'checking', orgNumber });
    try {
      const response = await ServiceSupplierSignup.checkOrg(orgNumber);
      if (request !== latestLookup.current) return;
      if (response.exists) {
        setLookup({ status: 'exists', orgNumber, admins: response.admins || [] });
        return;
      }
      setLookup({
        status: 'available',
        orgNumber,
        synaName: response.companyName,
        address: {
          address: response.address || '',
          city: response.city || '',
          postalCode: response.postalCode || '',
        },
      });
    } catch {
      if (request === latestLookup.current) setLookup({ status: 'failed', orgNumber });
    }
  };

  // A prefilled org number never went through the typed field, so it is looked up on arrival.
  useEffect(() => {
    if (lookup.status === 'idle' && showsTypedOrgNumber) lookUp(typedOrgNumber);
  }, []);

  const forgetCompany = () => {
    latestLookup.current += 1;
    setLookup({ status: 'idle' });
    setFormatError('');
    setCompanyName('');
    updateState({
      companyPick: null,
      organizationNumber: '',
      companyName: '',
      address: '',
      postalCode: '',
      city: '',
      completedSteps: state.completedSteps.filter(step => step !== 1),
    });
  };

  const pickCompany = (company: CompanySearchRow | null) => {
    forgetCompany();
    setPick(company);
    setTypedOrgNumber('');
    if (!company) return;
    setCompanyName(company.name);
    lookUp(company.orgNumber);
  };

  const changeTypedOrgNumber = (value: string) => {
    const formatted = formatOrgNumber(value);
    setTypedOrgNumber(formatted);
    if (formatted === organizationNumber) return;
    forgetCompany();
    setPick(null);
    if (ORG_NUMBER_REGEX.test(formatted)) lookUp(formatted);
  };

  const checkOrgNumberFormat = () => {
    if (organizationNumber && !ORG_NUMBER_REGEX.test(organizationNumber)) {
      setFormatError(
        t('company.orgNumberFormat', 'Organisationsnumret måste ha formatet XXXXXX-XXXX'),
      );
    }
  };

  const handleNext = () => {
    if (!checked || !canProceed) return;

    updateState({
      companyPick: pick,
      organizationNumber,
      companyName: companyNameToSave.trim(),
      ...checked.address,
      termsAcceptedAt: state.termsAcceptedAt || new Date().toISOString(),
    });

    completeStep(1);
    trackEvent('signup_wizard_step_company');
    setCurrentStep(2);
    history.push(withSource('/supplier-sign-up/categories'));
  };

  const orgExistsAlert = existingAdmins && (
    <ErrorAlert title={t('company.orgExists', 'Det här företaget har redan ett konto i Accurator')}>
      {existingAdmins.length > 0 && (
        <>
          <p className={styles.adminsLabel}>
            {t('company.adminsLabel', 'Följande administratörer är kopplade till kontot:')}
          </p>
          <ul className={styles.adminList}>
            {existingAdmins.map((admin, i) => (
              <li key={i}>{admin.email}</li>
            ))}
          </ul>
        </>
      )}
      <p className={styles.takeoverInfo}>
        {t(
          'company.takeoverInfo',
          'Vill du ta över denna organisation, men saknar kontakt med nuvarande administratörer? Kontakta oss så hjälper vi dig vidare.',
        )}
      </p>
      <div className={styles.support}>
        <p className={styles.supportTitle}>{t('company.support', 'Support')}</p>
        <p>
          <a href="mailto:support@accurator.se" className={styles.supportLink}>
            support@accurator.se
          </a>
        </p>
        <p>
          <a href="tel:+4608156225" className={styles.supportLink}>
            08 156-225
          </a>
        </p>
      </div>
    </ErrorAlert>
  );

  const nameField = nameIsEditable ? (
    <div>
      <Field
        id="company-name"
        value={companyName}
        onChange={setCompanyName}
        placeholder={t('company.companyNamePlaceholder', 'Ange företagsnamn')}
        label={t('company.companyName', 'Företagsnamn')}
        required
        ariaDescribedBy={pick ? nameHelperId : undefined}
      />
      {pick && (
        <p id={nameHelperId} className={styles.nameHelper}>
          {t(
            'company.nameHelper',
            'Hämtat från företagsregistret. Ändra om ni handlar under ett annat namn.',
          )}
        </p>
      )}
    </div>
  ) : (
    <Field
      id="company-name"
      value={
        checking
          ? t('company.searchingCompany', 'Söker företag...')
          : companyNameToSave ||
            t('company.companyNameAuto', 'Fylls i automatiskt från organisationsnummer')
      }
      label={t('company.companyName', 'Företagsnamn')}
      required
      disabled
    />
  );

  return (
    <WizardLayout showProgressDots={false}>
      <div className={`flex flex-col gap-6 ${styles.step}`}>
        <StepHeader
          title={t('company.title', 'Skapa ert kostnadsfria leverantörskonto')}
          stepNumber={1}
          description={t('company.description', 'Företagsuppgifter')}
        />

        <div>
          {down === 'unavailable' && <CompanySearchUnavailable />}
          {typed ? (
            <div className="relative">
              <Field
                ref={typedFieldRef}
                id="org-number"
                value={organizationNumber}
                preserveCaret
                onChange={changeTypedOrgNumber}
                onBlur={checkOrgNumberFormat}
                placeholder="XXXXXX-XXXX"
                label={t('company.orgNumber', 'Organisationsnummer')}
                required
                errorMessage={formatError || lookupError}
              />
              {checking && (
                <div className={styles.orgNumberSpinner}>
                  <Spinner progress={0} size="xxs" />
                </div>
              )}
            </div>
          ) : (
            <CompanySearch
              value={pick}
              onChange={pickCompany}
              onUnavailable={onUnavailable}
              label={t('company.company', 'Företag')}
              required
              errorMessage={lookupError}
              cardClassName={styles.companyCard}
            />
          )}
        </div>

        {orgExistsAlert}

        {(typed || pick) && nameField}

        <label className="flex items-center gap-1 cursor-pointer select-none">
          <StyledCheckbox
            checked={termsAccepted}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTermsAccepted(e.target.checked)
            }
          />
          <span className={styles.terms}>
            {t('company.termsPrefix', 'Jag godkänner Accurators')}{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className={styles.link}>
              {t('company.termsLink', 'användaravtal')}
            </a>
          </span>
        </label>

        <div className="flex justify-end">
          <BtButton onClick={handleNext} disabled={!canProceed} loaderShow={!!pick && checking}>
            {t('common.next', 'Nästa')} &rarr;
          </BtButton>
        </div>

        <p className={styles.hasAccount}>
          {t('company.hasAccount', 'Har du redan ett konto?')}{' '}
          <a href="/" className={styles.link}>
            {t('company.login', 'Logga in')}
          </a>
        </p>
      </div>
    </WizardLayout>
  );
};

export default StepCompany;
